import React, { useState } from "react";
import PropTypes from "prop-types";
import HomepageTitle from "arena/components/HomepageTitle";
import HeaderMoreLink from "arena/components/HeaderMoreLink";
import ShelfTitleLink from "arena/components/ShelfTitleLink/ShelfTitleLink";
import ProductList from "arena/components/ProductList";
import ProductCarousel from "arena/components/ProductCarousel";
import ProductPromoVideoDialog from "arena/components/ProductPromoVideoDialog";
import ErrorScreen from "core/components/ErrorScreen";
import Box from "@mui/material/Box";
import { ProductsQuery } from "arena/queries";
import { useQuery } from "@apollo/client";
import analytics from "core/utils/analytics";

export default function ArenaProducts({
  arena,
  limit = 12,
  title,
  loadMore,
  type,
  rightholderIds,
  view,
  moreUrlHref,
}) {
  const [product, setProduct] = useState();

  const variables = {
    arenaId: arena.id,
    rowsPerPage: limit,
    type,
  };

  if (rightholderIds?.length) {
    variables.rightholderIds = rightholderIds;
  }

  const { data, loading, error, fetchMore } = useQuery(ProductsQuery, {
    fetchPolicy: "cache-and-network",
    variables,
  });

  if (error) return <ErrorScreen error={error} />;

  const nodes = data?.products?.nodes || [];
  const count = data?.products?.count || 0;

  if (loading && !data) {
    return null;
  }

  if (!nodes || nodes.length === 0) {
    return null;
  }

  const onClosePromoVideoDialog = () => setProduct();
  const onClickPromoVideoButton = (product) => setProduct(product);

  const hasMore = nodes.length < count;
  const showLoadMore = loadMore && hasMore && !loading;
  const nextPage = Math.ceil(nodes.length / limit);

  const onLoadMore = () => {
    analytics.event({
      category: "User",
      action: "Load more products",
      label: nextPage + " page",
    });

    fetchMore({
      variables: {
        page: nextPage,
      },
    });
  };

  const components = {
    CAROUSEL: ProductCarousel,
    GRID: ProductList,
  };

  const List = components[view] || ProductList;

  const moreLink = moreUrlHref?.trim() || null;

  let titleElement = title;
  if (moreLink && typeof title === "string") {
    titleElement = <ShelfTitleLink url={moreLink}>{title}</ShelfTitleLink>;
  }

  const homepageTitle = title ? (
    <HomepageTitle
      title={titleElement}
      action={moreLink && <HeaderMoreLink url={moreLink} />}
    />
  ) : (
    <Box sx={{ mt: 2 }} />
  );

  return (
    <>
      <List
        title={homepageTitle}
        rowsPerPage={limit}
        loadMore={loadMore}
        loading={loading}
        nodes={nodes}
        count={count}
        onLoadMore={onLoadMore}
        showLoadMore={showLoadMore}
        nextPage={nextPage}
        onClickPromoVideoButton={onClickPromoVideoButton}
      />
      {product && (
        <ProductPromoVideoDialog
          onClose={onClosePromoVideoDialog}
          open={!!product}
          product={product}
        />
      )}
    </>
  );
}

ArenaProducts.propTypes = {
  view: PropTypes.oneOf(["CAROUSEL", "GRID"]),
  arena: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  type: PropTypes.string,
  limit: PropTypes.number,
  title: PropTypes.string,
  rightholderIds: PropTypes.arrayOf(PropTypes.string),
  loadMore: PropTypes.bool,
  moreUrlHref: PropTypes.string,
};
